import { useHandler } from "@redotech/react-util/hook";
import ArrowLeftIcon from "@redotech/redo-icons/arrow-left_filled.svg";
import ArrowRightIcon from "@redotech/redo-icons/arrow-right_filled.svg";
import { FrontendProduct } from "@redotech/redo-model/agents/support-agents/frontend-product";
import { filterTruthy } from "@redotech/util/array";
import classNames from "classnames";
import "keen-slider/keen-slider.min.css";
import { useKeenSlider } from "keen-slider/react";
import {
  memo,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { RedoSpinner } from "../arbiter-components/loading/redo-spinner";
import { Flex } from "../flex";
import { Text } from "../text";
import * as productCarouselCss from "./product-carousel.module.css";
import { ProductTile, ProductTileProps } from "./product-tile";
import * as productTileCss from "./product-tile.module.css";

type ProductTilePropsConfig = Omit<
  ProductTileProps,
  "product" | "topRightButton"
> & { topRightButton?: (product: FrontendProduct) => ReactNode };

interface ProductCarouselProps {
  products: FrontendProduct[];
  loading?: boolean;
  error?: string;
  productTileProps: ProductTilePropsConfig;
}

export const ProductCarousel = memo(function ProductCarousel({
  products,
  loading = false,
  error,
  productTileProps,
}: ProductCarouselProps) {
  if (loading) {
    return (
      <Flex
        className={classNames(productCarouselCss.productCarouselContainer)}
        justify="flex-start"
        position="relative"
      >
        <Carousel>
          {Array.from({ length: 3 }).map((_, index) => (
            <ProductLoadingTile key={index} />
          ))}
        </Carousel>
      </Flex>
    );
  }

  if (error) {
    return (
      <Flex justify="center" p="lg">
        <Text color="error">{error}</Text>
      </Flex>
    );
  }

  if (!products.length) {
    return null;
  }

  const { onImageClick, topRightButton } = productTileProps;

  // If there's only one product, render it directly without the carousel
  if (products.length === 1) {
    const product = products[0];
    return (
      <Flex justify="center" position="relative" w="full">
        <ProductTile
          buttonText={productTileProps.buttonText}
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          key={product.externalId}
          onButtonClick={productTileProps.onButtonClick}
          onImageClick={onImageClick}
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          product={product}
          readOnly={productTileProps.readOnly}
          showQuantity={productTileProps.showQuantity}
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          topRightButton={topRightButton ? topRightButton(product) : undefined}
        />
      </Flex>
    );
  }

  return (
    <Flex
      className={classNames(productCarouselCss.productCarouselContainer)}
      justify="flex-start"
      position="relative"
    >
      <Carousel>
        {products.map((product) => (
          <ProductTile
            buttonText={productTileProps.buttonText}
            key={product.externalId}
            onButtonClick={productTileProps.onButtonClick}
            onImageClick={onImageClick}
            product={product}
            readOnly={productTileProps.readOnly}
            showQuantity={productTileProps.showQuantity}
            topRightButton={
              topRightButton ? topRightButton(product) : undefined
            }
          />
        ))}
      </Carousel>
    </Flex>
  );
});

const ProductLoadingTile = memo(function ProductLoadingTile() {
  return (
    <Flex
      className={productTileCss.productTileContainer}
      dir="column"
      gap="none"
      py="10xl"
      radius="sm"
    >
      <Flex
        align="center"
        className={productTileCss.productTileImageContainer}
        justify="center"
      >
        <RedoSpinner size="md" />
      </Flex>
    </Flex>
  );
});

export const Carousel = memo(function Carousel({
  children,
}: {
  children: React.ReactNode;
}) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [details, setDetails] = useState<any>(null);

  const childrenArray = useMemo(() => {
    if (!children) return [];
    const arr = Array.isArray(children) ? children : [children];
    return filterTruthy(arr);
  }, [children]);

  const [sliderRef, instanceRef] = useKeenSlider(
    {
      selector: ".keen-slider__slide",
      loop: false,
      mode: "snap",
      slides: { origin: "center", perView: 1.4, spacing: 5 },
      slideChanged(slider) {
        setCurrentSlide(slider.track.details.rel);
      },
      detailsChanged(slider) {
        setDetails(slider.track.details);
      },
      created(slider) {
        slider.moveToIdx(0, true);
      },
    },
    [],
  );

  // Update slider when children change to recalculate sizes
  useEffect(() => {
    if (instanceRef.current && childrenArray.length > 0) {
      instanceRef.current.update();
    }
  }, [childrenArray.length, instanceRef]);

  const shouldShowRightArrow = useMemo(() => {
    return currentSlide < childrenArray.length - 1;
  }, [currentSlide, childrenArray.length]);

  const shouldShowLeftArrow = useMemo(() => {
    return currentSlide > 0;
  }, [currentSlide]);

  const getSlideClass = useCallback(
    (index: number) => {
      if (index === currentSlide) {
        return productCarouselCss.currentSlide;
      } else if (Math.abs(index - currentSlide) === 1) {
        return productCarouselCss.adjacentSlide;
      } else {
        return productCarouselCss.otherSlide;
      }
    },
    [currentSlide],
  );

  function scaleStyle(idx: number) {
    if (!details || !details.slides || !details.slides[idx]) return {};
    const slide = details.slides[idx];
    const scale_size = 0.1;
    const opacity_size = 0.8;
    const scale = 1 - (scale_size - scale_size * slide.portion);
    const opacity = 1 - (opacity_size - opacity_size * slide.portion);
    return {
      transform: `scale(${scale})`,
      WebkitTransform: `scale(${scale})`,
      opacity,
    };
  }

  // Return null if no children - after all hooks
  if (childrenArray.length === 0) {
    return null;
  }

  return (
    <>
      <div
        className={classNames(
          "keen-slider",
          childrenArray.length === 1 && productCarouselCss.sliderOverflow,
        )}
        ref={sliderRef}
      >
        {childrenArray.map((child, index) => (
          <div
            className={classNames(
              "keen-slider__slide",
              productCarouselCss.carouselElementContainer,
              productCarouselCss.sliderOverflow,
              getSlideClass(index),
            )}
            key={index}
          >
            <Flex style={scaleStyle(index)} w="full">
              {child}
            </Flex>
          </div>
        ))}
      </div>
      {shouldShowLeftArrow && (
        <div
          className={classNames(
            productCarouselCss.carouselArrowButton,
            productCarouselCss.left,
          )}
        >
          <ArrowButton
            ArrowIcon={ArrowLeftIcon}
            onClick={() => instanceRef.current?.prev()}
          />
        </div>
      )}
      {shouldShowRightArrow && (
        <div
          className={classNames(
            productCarouselCss.carouselArrowButton,
            productCarouselCss.right,
          )}
        >
          <ArrowButton
            ArrowIcon={ArrowRightIcon}
            onClick={() => instanceRef.current?.next()}
          />
        </div>
      )}
    </>
  );
});

const ArrowButton = memo(function ArrowButton({
  ArrowIcon,
  onClick,
}: {
  ArrowIcon: React.JSXElementConstructor<any>;
  onClick: () => void;
}) {
  const handleClick = useHandler((event: React.MouseEvent) => {
    event.stopPropagation();
    onClick();
  });

  return (
    <Flex
      align="center"
      as="button"
      className={productCarouselCss.arrowButtonContainer}
      justify="center"
      onClick={handleClick}
      radius="full"
    >
      <ArrowIcon className={productCarouselCss.arrowButtonIcon} />
    </Flex>
  );
});
